import React from 'react'
import storage from '@/utils/storage'
import getBackendLanguageCode from '@/utils/language'
import type { FilterContentSelection, SelectOption } from './types/Filter'
import type {
    AdvancedSendRequestParams,
    ChatCompletionAdapter,
    CustomCallAdapter,
    CustomCallResponse,
    HandlerAssistant,
    MetadataAdapter,
    MMAHandlerState,
} from './types/MMAHandlers'

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value)

const isCustomCallAdapter = (value: unknown): value is CustomCallAdapter =>
    isRecord(value) && typeof value.sendCustomCall === 'function'

const isChatCompletionAdapter = (value: unknown): value is ChatCompletionAdapter =>
    isRecord(value) && typeof value.getChatCompletions === 'function'

const isMetadataAdapter = (value: unknown): value is MetadataAdapter =>
    isRecord(value) && typeof value.getMetadata === 'function'

export function useMMAHandlers(mmaState: MMAHandlerState) {
    const {
        activeAssistant,
        activeSpace,
        information,
        i18n,
        currentFilters,
        setCurrentFilters,
        navigateTo,
        setGenericStoreValue,
        getGenericStoreValue,
        switchAssistant,
        showModal,
        hideModal,
        changeConfirmButtonState,
        shiftbookState,
        setShiftbookState,
        validateTokenAndRehydrate,
        activeMessageList,
        t,
        getApiToken,
    } = mmaState

    const getMmaParameters = (): Record<string, unknown> => {
        const parameters = getGenericStoreValue('mmaParameters')
        return isRecord(parameters) ? parameters : {}
    }

    const sendFeedback = async (
        messageId: string | undefined,
        feedbackType: string | undefined,
        feedbackText: string,
        checkboxes: Record<string, boolean>
    ) => {
        console.warn('Not implemented: Feedback: ', messageId, feedbackType, feedbackText, checkboxes)
    }

    const showShiftbookModal = (shiftbookContent: string, messageId: string) => {
        setShiftbookState({
            state: 'shiftContent',
            content: shiftbookContent,
            messageId: messageId
        })
    }

    const sendToShiftbook = async (): Promise<CustomCallResponse | undefined> => {
        let response: CustomCallResponse | undefined
        const formData = new FormData()

        if (shiftbookState?.content) {
            await validateTokenParams()

            const token = await getApiToken()

            const params = {
                ...getMmaParameters(),
                chatId: activeAssistant?.activeChat?.chatId,
                messageId: shiftbookState?.messageId,
                token,
            }

            formData.append('message', shiftbookState?.content)

            const url = '/spaces/{space}/assistants/{assistant}/action/transmit-message/{messageId}'
            const options: Record<string, unknown> = {
                method: 'POST',
                body: formData,
                redirect: 'follow',
            }

            response = await sendCustomCallWithRetry(
                activeAssistant?.aiAdapter,
                url,
                options,
                params
            )
        }

        return response
    }

    const showHandoverModal = async (messageId: string) => {
        const selectedLine = { value: '', label: '' }
        const newAssistantId = `${activeSpace?.id}-documentation`
        const newAssistant = activeSpace?.assistants?.find(assistant => assistant.config?.id === newAssistantId)
        const allParameters = getMmaParameters()

        if (newAssistant && !newAssistant.metadata) {
            const token = await getApiToken()
            const params = {
                ...allParameters,
                assistantEndpoint: newAssistant.config?.endpoint,
                object: 'chatFilter',
                space: activeSpace?.endpoint,
                token,
            }

            if (!isMetadataAdapter(newAssistant.aiAdapter)) {
                throw new Error('Assistant does not support metadata requests')
            }
            const metadataResponse = await newAssistant.aiAdapter.getMetadata(params)
            newAssistant.metadata = metadataResponse.metadata
        }

        changeConfirmButtonState(false)
        showModal({
            title: t('handover:title'),
            confirmLabel: t('general:buttons:confirm'),
            cancelLabel: t('general:buttons:cancel'),
            content: React.createElement('div', { className: 'handover-modal' },
                React.createElement(React.Fragment, null, '/* HandoverModal must be imported in the parent file if used here */')
            ),
            onConfirmClick: async () => {
                if (messageId) {
                    hideModal()
                    await handoverToAssistant(messageId, selectedLine)
                }
            },
            onCancelClick: hideModal,
        })
    }

    const handoverToAssistant = async (messageId: string, selectedLine: SelectOption) => {
        const allParameters = getMmaParameters()
        let response: Record<string, unknown> | undefined

        await validateTokenParams()

        const token = await getApiToken()

        const persistableParams = {
            ...allParameters,
            assistant: activeAssistant?.config?.endpoint,
            space: activeSpace?.endpoint,
        }
        const params = { ...persistableParams, token }

        const query = {
            userID: information.userId,
            chatID: activeAssistant?.activeChat?.chatId,
            messageID: messageId,
            languageCode: getBackendLanguageCode(i18n?.language, activeSpace?.additionalBackendSupportedLanguages),
        }

        if (messageId) {
            const url = '/spaces/{space}/assistants/{assistant}/handoverTo/issue-documentation'
            const options: Record<string, unknown> = {
                method: 'GET',
                redirect: 'follow',
            }

            const customCallResponse = await sendCustomCallWithRetry(
                activeAssistant?.aiAdapter,
                url,
                options,
                params,
                query
            )
            if (!customCallResponse) throw new Error('Handover request returned no response')
            const responseBody = await customCallResponse.json()
            if (!isRecord(responseBody)) throw new Error('Handover response is not an object')
            response = responseBody

            setGenericStoreValue('mmaParameters', {
                ...persistableParams,
                line: selectedLine.value,
            })

            const newAssistantId = `${activeSpace?.id}-documentation`
            const newAssistant = activeSpace?.assistants?.find(assistant => assistant.config?.id === newAssistantId)

            if (newAssistant) newAssistant.suppressNextWelcomeMessage = true
            await switchAssistant(newAssistantId)
            await newAssistant?.switchChat?.(newAssistantId)

            const filterSelection = {
                line: [{ value: selectedLine.value, label: selectedLine.label }],
                source: [],
            }

            setGenericStoreValue('handOverFilter', filterSelection)

            // The line of a handover is not a preferred line: the pills show it for this chat, and only the user's
            // own save of the line dialog makes it one

            navigateTo(`/${activeSpace?.id}/${newAssistant?.path}?handOver=true`)

            await advancedSendRequest({
                message: typeof response.fakeUserMessage === 'string' ? response.fakeUserMessage : '',
                line: selectedLine.value,
                newParams: { ...params, chatId: response.chatId },
                newAssistant,
                isHandOver: true
            })
        }

        return response
    }

    const saveSelection = (selection: FilterContentSelection, persist: boolean, newAssistant?: HandlerAssistant) => {
        const allParameters = getMmaParameters()
        const parsedSelection = {
            line: selection.line.map(line => line.value).join(','),
            source: selection.source.join(','),
        }

        const assistantIdForSelection = newAssistant?.config?.id || activeAssistant?.config?.id

        if (assistantIdForSelection) {
            const compact: { line: string[]; source: string[] } = {
                line: selection.line.map(line => line.value),
                source: selection.source,
            }

            try {
                sessionStorage.setItem(`mmaFilters:${assistantIdForSelection}`, JSON.stringify(compact))
            } catch {
                // ignore
            }
        }

        if (persist && assistantIdForSelection) {
            setGenericStoreValue('savedFilters', { [assistantIdForSelection]: selection }, true)
        }

        if (assistantIdForSelection?.endsWith('-resolution')) {
            storage.setSelectedResolutionSources(selection.source)
        }

        setCurrentFilters(selection)
        setGenericStoreValue('mmaParameters', {
            ...allParameters,
            ...parsedSelection
        })
    }

    const validateTokenParams = async (message?: string) => {
        const { chatId, messageList } = activeAssistant?.activeChat || {}
        const activeMessagesCount = messageList?.filter(
            message => message.role !== 'assistant' || message.messageId || message.message_id
        ).length

        const dataToRehydrate = {
            chatId,
            activeMessagesCount,
            currentFilters,
            message
        }

        return await validateTokenAndRehydrate(dataToRehydrate)
    }

    const sendCustomCallWithRetry = async (
        adapter: unknown,
        url: string,
        options: Record<string, unknown>,
        params: Record<string, unknown>,
        query?: Record<string, unknown>
    ): Promise<CustomCallResponse | undefined> => {
        if (!isCustomCallAdapter(adapter)) throw new Error('Assistant does not support custom calls')

        const response = await adapter.sendCustomCall(url, options, params, query)

        if (response?.status === 401 && !params._retryOnUnauthorized) {
            const refreshedToken = await getApiToken(true)
            const retryParams = {
                ...params,
                token: refreshedToken,
                _retryOnUnauthorized: true,
            }

            return await adapter.sendCustomCall(url, options, retryParams, query)
        }

        return response
    }

    const buildFormData = (
        message: string,
        language: string | undefined,
        isGlobalSearch?: boolean,
        attachment?: AdvancedSendRequestParams['attachment'],
        file_urls?: string[],
        preview_urls?: string[],
        params?: Record<string, unknown>
    ) => {
        const formData = new FormData()
        formData.append('message', message)
        formData.append('role', 'user')
        formData.append('languageCode', getBackendLanguageCode(language, activeSpace?.additionalBackendSupportedLanguages))

        if (isGlobalSearch) formData.append('searchDepth', '2')

        if (attachment?.file) {
            formData.append('attachment', attachment.file, attachment.file.name)
            formData.append('attachmentSource', attachment.source || 'unknown')
            if (attachment.base64) formData.append('attachmentBase64', attachment.base64)
        }

        if (file_urls?.length) formData.append('file_urls', file_urls.join(','))
        if (preview_urls?.length) formData.append('preview_urls', preview_urls.join(','))
        
        if (params?.additionalParams) {
            const additional = params.additionalParams
            formData.append('additionalParams', typeof additional === 'object' ? JSON.stringify(additional) : String(additional))
        }

        return formData
    }

    const finalizeParamsForAssistant = (
        params: Record<string, unknown>,
        newAssistant?: HandlerAssistant,
        newParams?: Record<string, unknown>
    ) => {
        if (!newAssistant) return params
        return {
            ...params,
            ...newParams,
            assistant: newAssistant.config?.endpoint,
        }
    }

    const appendLineAndSource = (
        formData: FormData,
        params: Record<string, unknown>,
        newAssistant?: HandlerAssistant,
        line?: string
    ) => {
        if (newAssistant) {
            if (line) formData.append('line', line)
            return
        }

        if (Object.hasOwn(params, 'line')) {
            if (typeof params.line === 'string' && params.line) formData.append('line', params.line)
        } else if (currentFilters?.line.length)
            formData.append('line', currentFilters.line.map(line => line.value).join(','))

        if (typeof params.source === 'string' && params.source) formData.append('source', params.source)
        else if (currentFilters?.source.length)
            formData.append('source', currentFilters.source.join(','))
    }

    const advancedSendRequest = async ({ message, line, newAssistant, newParams, attachment, file_urls, preview_urls, isGlobalSearch, isHandOver, isRetry }: AdvancedSendRequestParams) => {
        const currentAssistant = newAssistant || activeAssistant

        let allParameters = getMmaParameters()
        if (newParams) allParameters = { ...allParameters, ...newParams }
        // Global search must use the line selection visible when the user clicked it, even when the generic
        // parameters still contain an older line. An empty string explicitly means the selection was cleared.
        if (isGlobalSearch && line !== undefined) allParameters = { ...allParameters, line }

        let params: Record<string, unknown> = {
            ...allParameters,
            chatId: newAssistant ? allParameters.chatId : activeAssistant?.activeChat?.chatId,
            assistant: activeAssistant?.config?.endpoint,
            userId: information.userId,
            additionalBackendSupportedLanguages: activeSpace?.additionalBackendSupportedLanguages,
        }

        const formData = buildFormData(message, i18n?.language, isGlobalSearch, attachment, file_urls, preview_urls, params)

        params = finalizeParamsForAssistant(params, newAssistant, newParams)
        appendLineAndSource(formData, params, newAssistant, line)

        // The token is checked while the message and the status of its answer already show (the adapter waits for
        // these params before it sends the request). An invalid token cancels the request.
        const paramsWithToken = (async () => {
            const isTokenValid = await validateTokenParams(message)
            if (!isTokenValid) return undefined

            const token = await getApiToken()
            return {
                ...params,
                token,
                retryOnUnauthorized: async () => ({ token: await getApiToken(true) }),
            }
        })()

        const onlyAssistantMessage = isGlobalSearch || isRetry || false
        if (!currentAssistant) return
        if (!isChatCompletionAdapter(currentAssistant.aiAdapter)) throw new Error('Assistant does not support chat completions')

        await currentAssistant.aiAdapter.getChatCompletions(
            activeMessageList,
            formData,
            undefined,
            paramsWithToken,
            onlyAssistantMessage,
            isHandOver
        )
    }

    return {
        handlers: {
            sendFeedback,
            showShiftbookModal,
            sendToShiftbook,
            showHandoverModal,
            handoverToAssistant,
            saveSelection,
            validateTokenParams,
            advancedSendRequest,
        }
    }
}
